import type { PureMeasureComponentProps, PureMeasureComponentState } from "@component/PureMeasureComponent";
import { PureMeasureComponent } from "@component/PureMeasureComponent";
import { Ad } from "@component/Widget/Ad/Ad";
import { faInfo as fadInfo } from "@awesome.me/kit-3777c2145e/icons/duotone/solid";
import {
	faArrowRight as fasArrowRight,
	faArrowDownWideShort as fasArrowDownWideShort,
	faPiggyBank as fasPiggyBank,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import { ResponsiveHelper } from "@utils/responsive-helper";
import autobind from "autobind-decorator";
import { Alert, FormSelect, Paginator, PromisePool, getUrlParameter, scrollToElement } from "bm-ui-kit";
import type { CommonOptions } from "bm-ui-kit";
import type { FormApi } from "final-form";
import React from "react";
import type { ReactNode } from "react";
import { Container, Form } from "react-bootstrap";
import { Form as FinalForm } from "react-final-form";
import Skeleton from "react-loading-skeleton";
import type { AbstractLeadFormProps } from "../AbstractLeadForm/AbstractLeadForm";
import type { ProductType } from "../AbstractLeadForm/types";
import type { AbstractResultItemProps } from "./AbstractResultItem";
import type { AbstractResponseItem, MarketingFilter, ResultListResponse } from "./types";

const ITEM_PER_PAGE = 10;

export interface AbstractResultListProps<
	SearchParams,
	Sort extends string,
	Request,
	ResponseItem extends AbstractResponseItem,
> extends PureMeasureComponentProps {
	searchParams: SearchParams;
	request: Request;
	results: ResultListResponse<ResponseItem>;
	error?: ReactNode;
	progress?: boolean;

	sort?: Sort;
	onSortChange?: (sort: Sort) => void;

	highlightedBank?: string;

	marketingFilter?: MarketingFilter;

	maxHighlight?: number;

	productType: ProductType;
	alwaysHasFullPromoList?: boolean; // Mobil nézetben ne csak egy promós terméket jelenítsen meg
}

export interface AbstractResultListState<ResponseItem extends AbstractResponseItem = AbstractResponseItem>
	extends PureMeasureComponentState {
	selected: number;
	showListingOffer: boolean;
	page: number;
	compareItems: ResponseItem[];
	showOtherBankResults: boolean;
	isMobile: boolean;
}

export interface OrderFields<Sort extends string = string> {
	sort: Sort;
}

export abstract class AbstractResultList<
	SearchParams,
	Sort extends string,
	Request,
	ResponseItem extends AbstractResponseItem,
	Props extends AbstractResultListProps<SearchParams, Sort, Request, ResponseItem> = AbstractResultListProps<
		SearchParams,
		Sort,
		Request,
		ResponseItem
	>,
	State extends AbstractResultListState = AbstractResultListState,
> extends PureMeasureComponent<Props, State> {
	private responsiveHelper;
	private readonly promisePool = new PromisePool();
	protected readonly compareEnabled: boolean;
	protected sortOptions: CommonOptions<Sort> = [];
	private mobileHighlightId = undefined;
	protected listingOfferButtonId: string;

	public constructor(props: Props) {
		super(props);

		this.mobileHighlightId = Math.floor(Math.random() * 3);
		this.state = this.getInitialState();
	}

	protected getInitialState(): State {
		this.responsiveHelper = new ResponsiveHelper("lg", (isOver) => {
			this.setState({
				isMobile: !isOver,
			});
		});

		return {
			selected: undefined,
			showSaveModal: false,
			showListingOffer: false,
			page: 1,
			compareItems: [],
			showOtherBankResults: false,
			isMobile: !this.responsiveHelper.getActualStatus(),
		} as unknown as State;
	}

	public componentDidMount() {
		this.responsiveHelper.startObserving();
	}

	public componentDidUpdate(prevProps: Props): void {
		const { results } = this.props;
		const { page, showOtherBankResults } = this.state;

		if (
			(page !== 1 || showOtherBankResults !== false) &&
			results &&
			JSON.stringify(prevProps.results) !== JSON.stringify(results)
		) {
			this.setState({
				page: 1,
				showOtherBankResults: false,
			});
		}
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
		this.responsiveHelper.stopObserving();
	}

	public render(): ReactNode {
		const { results, error, progress, highlightedBank } = this.props;

		if (progress) {
			return (
				<div id="result" className="result-container pt-4">
					<Container>
						<Skeleton className="mt-3" count={ITEM_PER_PAGE} height="6em" />
					</Container>
				</div>
			);
		}

		if (results === undefined) {
			return null;
		}

		if ((results.highlightList.length === 0 && results.normalList.length === 0) || error) {
			return (
				<div id="result" className="result-container pt-4">
					<Container>{this.renderError()}</Container>
				</div>
			);
		}

		return (
			<div id="result" className="result-container pt-4">
				{highlightedBank ? this.renderBankSpecificList() : this.renderTotalList()}
				{this.renderAfterListBlock()}
			</div>
		);
	}

	protected renderHighlightedBankNoResults(): ReactNode {
		return (
			<Alert icon="auto" size="lg" variant="danger">
				Sajnos a megadott beállítások mellett az adott banknak nincs elérhető ajánlat!
				<br />
				Nézd meg a többi bank ajánlatát!
			</Alert>
		);
	}

	protected renderError(): ReactNode {
		const { error = "Sajnos a megadott beállítások mellett nincs elérhető ajánlat!" } = this.props;

		return (
			<Alert icon="auto" size="lg" variant="danger">
				{error}
			</Alert>
		);
	}

	private renderTotalList(): ReactNode {
		const { results } = this.props;
		const { page } = this.state;

		const startFrom = (page - 1) * ITEM_PER_PAGE;
		const slicedResults = results.normalList.slice(startFrom, page * ITEM_PER_PAGE);

		this.sendViewItemListEvent(slicedResults, "product", "view_item_list");

		return (
			<>
				{this.renderHighlightList()}
				{this.renderHeader(this.renderNormalListTitle())}
				<div id="result-normal">
					{this.insertOffer(
						slicedResults.map((result, index) => (
							<Container key={`${result.id || index}`}>
								{this.renderResult(this.getResultProps(result, index + startFrom + 1))}
							</Container>
						)),
					)}
				</div>
				{this.renderPaginator()}
			</>
		);
	}

	protected renderHighlightList(): ReactNode {
		const { results, maxHighlight = 3, alwaysHasFullPromoList = false } = this.props;
		const { isMobile } = this.state;

		if (!results?.highlightList?.length) {
			return null;
		}

		// Random válasszunk egy elemet, ami mobilon is ki van emelve
		if (this.mobileHighlightId >= results?.highlightList?.length) {
			this.mobileHighlightId = Math.floor(Math.random() * Math.min(results.highlightList.length, maxHighlight));
		}
		const slicedResults = results.highlightList.slice(0, maxHighlight);
		this.sendViewItemListEvent(slicedResults, "ad", "view_item_list");

		return (
			<>
				{results.highlightList.length > 2 && this.renderCustomizationAlert()}
				{this.renderHighlightListTitle()}
				{slicedResults.map((result, index) => {
					if (isMobile && this.mobileHighlightId !== index && !alwaysHasFullPromoList) {
						return null;
					}

					return (
						<Container key={`promo-${result.id || index}`}>
							{this.renderResult(this.getResultProps(result, -1 * (index + 1)))}
						</Container>
					);
				})}
			</>
		);
	}

	protected renderBankSpecificList(): ReactNode {
		const { results, highlightedBank } = this.props;
		const { page, showOtherBankResults } = this.state;

		const fullList = results.normalList.map((item, index) => ({ ...item, position: index }));

		const bankList = fullList.filter((item) => item.bank.code === highlightedBank);
		const otherList = fullList.filter((item) => item.bank.code !== highlightedBank);

		const startFrom: number = (page - 1) * ITEM_PER_PAGE;
		const slicedResults = showOtherBankResults
			? otherList.slice(startFrom, page * ITEM_PER_PAGE)
			: otherList.slice(0, 1);

		this.sendViewItemListEvent(slicedResults, "product", "view_item_list");

		return (
			<>
				{this.renderHeader(this.renderBankSpecifictTitle())}
				{bankList.length === 0 && (
					<div id="result" className="result-container pt-4">
						<Container>{this.renderHighlightedBankNoResults()}</Container>
					</div>
				)}
				{bankList.map((result) => (
					<Container key={`${result.id || result.position}`}>
						{this.renderResult(this.getResultProps(result, result.position + 1))}
					</Container>
				))}
				<div className="result-header mt-5 mb-4 text-center">
					<div className="h2">{this.renderOtherBankTitle()}</div>
				</div>
				{slicedResults.map((result) => (
					<Container key={`${result.id || result.position}`}>
						{this.renderResult(this.getResultProps(result, result.position + startFrom + 1))}
					</Container>
				))}
				{showOtherBankResults ? (
					otherList.length > ITEM_PER_PAGE && (
						<Container>
							<Paginator
								activePage={page}
								itemPerPage={ITEM_PER_PAGE}
								itemCount={otherList.length}
								onChange={this.onPaginatorChange}
							/>
						</Container>
					)
				) : (
					<Container className="buttons text-center">
						<button
							type="button"
							className="show-more mb-3 text-uppercase"
							onClick={() => {
								this.setState({
									showOtherBankResults: true,
								});
							}}
						>
							Mutasd a többi bank ajánlatát is{" "}
							<FontAwesomeIcon icon={fasArrowRight} className="ml-2" rotation={90} />
						</button>
					</Container>
				)}
			</>
		);
	}

	protected renderAfterListBlock(): ReactNode {
		return null;
	}

	protected getDefaultOrder(): OrderFields<Sort> {
		let { sort } = this.props;

		if (!sort) {
			sort = this.sortOptions?.length ? this.sortOptions[0].value : undefined;
		}

		return {
			sort,
		};
	}

	protected getListingOfferLeadProps(): AbstractLeadFormProps<any, any, SearchParams> {
		const { searchParams } = this.props;

		return {
			searchParams,
			fluid: true,
			ga4Event: { buttonId: this.listingOfferButtonId, ...this.getCommonEventParameters() },
		};
	}

	protected renderCustomizationAlert(): ReactNode {
		const customizationAlert = getUrlParameter("withCustomizationAlert", undefined);

		if (customizationAlert) {
			return (
				<div className="text-center">
					<a
						className="d-inline-block mx-auto text-decoration-none"
						href="#calculator"
						onClick={() => {
							GoogleAnalytics4.sendEvent("BM_scrollTo", {
								buttonName: "customizeCalculation",
							});
							scrollToElement("calculator-container");
						}}
					>
						<Alert icon={<FontAwesomeIcon icon={fadInfo} />} className="mb-0" variant="warning" size="lg">
							<strong>Személyre szabott ajánlatokért </strong>
							ide kattintva pontosíthatod a keresési feltételeket.
						</Alert>
					</a>
				</div>
			);
		}
	}

	protected renderHighlightListTitle(): ReactNode {
		return null;
	}

	protected renderNormalListTitle(): ReactNode {
		return null;
	}

	protected renderBankSpecifictTitle(): ReactNode {
		return null;
	}

	protected renderOtherBankTitle(): ReactNode {
		return null;
	}

	protected renderHeader(title: ReactNode): ReactNode {
		return (
			<FinalForm<OrderFields<Sort>>
				initialValues={this.getDefaultOrder()}
				validateOnBlur={false}
				onSubmit={this.onUpdatePagination}
			>
				{({ handleSubmit }) => (
					<Form className="container result-order" onSubmit={handleSubmit}>
						<div className="result-header mt-5 mb-4 text-center">
							<div className="h2">{title}</div>
						</div>
						{this.renderCustomizationAlert()}
						{this.sortOptions?.length > 0 && (
							<div className="d-flex mt-4 align-items-center">
								<div className="font-weight-bold mr-2">Rendezés:</div>
								<div>
									<FormSelect
										className="mb-0"
										name="sort"
										prepend={<FontAwesomeIcon icon={fasArrowDownWideShort} />}
										onChange={async () => handleSubmit()}
										options={this.sortOptions}
									/>
								</div>
							</div>
						)}
					</Form>
				)}
			</FinalForm>
		);
	}

	@autobind
	protected onUpdatePagination(values: OrderFields<Sort>, form: FormApi<OrderFields<Sort>>): void {
		const { sort: oldSort, onSortChange } = this.props;
		const { sort } = values;

		form.pauseValidation();

		if (oldSort !== sort) {
			onSortChange(sort);
		}
	}

	protected getResultProps(
		result: ResponseItem,
		index: number,
	): AbstractResultItemProps<SearchParams, Request, ResponseItem> {
		const { searchParams, request, sort, productType } = this.props;
		const { selected } = this.state;

		return {
			index,
			searchParams: { sort, ...searchParams },
			request,
			result,
			selected: selected === index,
			onSelect: this.onSelect,
			ga4Event: this.getCommonEventParameters(),
			productType,
			sendViewItemEvent: () => {
				this.sendViewItemListEvent([result], result?.marketing?.highlight ? "ad" : "product", "view_item");
			},
		};
	}

	protected abstract renderResult(props: AbstractResultItemProps<SearchParams, Request, ResponseItem>): ReactNode;

	protected renderOwnPromotion(): ReactNode {
		return (
			<div className="d-flex align-items-center after-3rd-item bg-white px-2 py-3 mx-auto" key="promotion">
				<div className="text-success border-right pr-2 mr-2">
					<FontAwesomeIcon icon={fasPiggyBank} size="3x" />
				</div>
				<div>
					Vizsgáld meg a <strong>20%-os állami támogatást (akár 280 ezer Ft/év)</strong> tartalmazó
					nyugdíjmegtakarítási lehetőségeket is:{" "}
					<a href="/nyugdij-megtakaritas/" target="_blank">
						nyugdíjmegtakarítás kalkulátor
					</a>{" "}
					–{" "}
					<a href="/megtakaritasok/nyugdijbiztositas/" target="_blank">
						nyugdíjbiztosítás
					</a>{" "}
					–{" "}
					<a href="/megtakaritasok/onkentes-nyugdijpenztar/" target="_blank">
						önkéntes pénztár
					</a>
				</div>
			</div>
		);
	}

	protected renderListingOffer(): ReactNode {
		return <div className="d-none" key="listing-offer" />;
	}

	protected renderPaginator(): ReactNode {
		const { results } = this.props;
		const { page } = this.state;

		if (results.normalList.length > ITEM_PER_PAGE) {
			return (
				<Container>
					<Paginator
						activePage={page}
						itemPerPage={ITEM_PER_PAGE}
						itemCount={results.normalList.length}
						onChange={this.onPaginatorChange}
					/>
				</Container>
			);
		}
	}

	@autobind
	protected onListingOfferClick(): void {
		const { showListingOffer } = this.state;

		this.listingOfferButtonId = GoogleAnalytics4.generateId("bi");

		this.sendEvent("BM_blockToggle", {
			buttonId: this.listingOfferButtonId,
			tab: undefined,
			dropdownName: "Egyeztess szakértőnkkel díjmentesen",
		});

		this.setState({
			showListingOffer: !showListingOffer,
		});
	}

	@autobind
	private onPaginatorChange(page: number, buttonType: "next" | "prev" | "pageNumber"): void {
		this.setState({
			page,
		});

		scrollToElement("result-normal");

		this.sendEvent("BM_pagination", { buttonType, pageNumber: page });
	}

	@autobind
	private onSelect(key: number): void {
		this.setState({
			selected: key,
		});
	}

	protected insertOffer(list: ReactNode): ReactNode {
		if (Array.isArray(list) && list.length > 2) {
			const newList: ReactNode[] = list.length > 3 ? [...list, this.renderAds2()] : list;

			newList.splice(3, 0, this.renderListingOffer());

			if (list.length > 7) {
				newList.splice(5, 0, this.renderAds1());
			}

			return newList;
		}

		return list;
	}

	protected renderAds1(): ReactNode {
		return (
			<div key="banner1">
				<Ad name="970x250 TOP1" code="5967603" width="970" height="250" type="desktop" priority={50} />
				<Ad
					name="300x250 MOBIL TOP1"
					code="5967598"
					width="300"
					height="250"
					type="non-desktop"
					priority={48}
				/>
			</div>
		);
	}

	protected renderAds2(): ReactNode {
		return (
			<div key="banner2">
				<Ad name="970x250 TOP2" code="5967604" width="970" height="250" type="desktop" priority={47} />
				<Ad
					name="300x250 MOBIL TOP2"
					code="5967599"
					width="300"
					height="250"
					type="non-desktop"
					priority={46}
				/>
			</div>
		);
	}

	@autobind
	protected onCompare(id: string, checked: boolean): void {
		const {
			results: { highlightList, normalList },
		} = this.props;
		let { compareItems } = this.state;
		const result = [...highlightList, ...normalList].find((item) => item.id === id);

		if (result) {
			if (!checked) {
				compareItems = compareItems.filter((item) => item.id !== id);
			} else {
				compareItems.push(result);
			}
			this.setState({
				compareItems,
			});
		}
	}

	@autobind
	protected compareButtonIsChecked(props: AbstractResultItemProps<SearchParams, Request, ResponseItem>): boolean {
		const {
			result: { id },
		} = props;
		const { compareItems } = this.state;

		return compareItems.find((item) => item.id === id) !== undefined;
	}

	protected sendViewItemListEvent(
		slicedResults: ResponseItem[],
		affiliation: "product" | "ad",
		type: "view_item_list" | "view_item",
		itemListName?: string,
	): void {
		const { ga4Event } = this.props;

		this.sendEvent(type, {
			item_list_id: ga4Event?.calculationId,
			item_list_name: itemListName || (affiliation === "ad" ? "promo" : "normal"),
			items: slicedResults.map((result, index) => ({
				item_id: result.id,
				item_brand: result?.bank?.name,
				item_name: result.name,
				affiliation,
				index,
				...this.createViewItemListEventItem(result),
			})),
			currency: type === "view_item" ? "HUF" : undefined,
		});
	}

	protected createViewItemListEventItem(_result: ResponseItem): Record<string, any> {
		return {};
	}
}
